import React, { createContext, useCallback, useMemo, useState } from "react";
import { CssBaseline, PaletteMode, ThemeProvider } from "@mui/material";
import { createAppTheme } from "common/theme";

const THEME_MODE_STORAGE_KEY = "flowenti-theme-mode";

export type ThemeModeContextType = {
  mode: PaletteMode;
  toggleThemeMode: () => void;
};

export const Context = createContext<ThemeModeContextType>({
  mode: "light",
  toggleThemeMode: () => undefined,
});

export const Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(() =>
    localStorage.getItem(THEME_MODE_STORAGE_KEY) === "dark" ? "dark" : "light"
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const toggleThemeMode = useCallback(() => {
    setMode((currentMode) => {
      const nextMode = currentMode === "dark" ? "light" : "dark";
      localStorage.setItem(THEME_MODE_STORAGE_KEY, nextMode);
      return nextMode;
    });
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggleThemeMode,
    }),
    [mode, toggleThemeMode]
  );

  return (
    <Context.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </Context.Provider>
  );
};
